import React, { memo, useMemo, useState, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { Button } from '@saleshandy/design-system';
import InlineAutosuggest, {
  InlineAutosuggestItem,
} from '../../../../../../../../shared/design-system/components/molecules/inline-autosuggest/inline-autosuggest';
import { LabelIcon } from '../../../../../../../../shared/svg';
import type { IProps } from './types';
import { getIsRequestPending } from '../../../../../../../../shared/utils';

const AssignPhoneNumberLabelModal: React.FC<IProps> = ({
  show,
  onClose,
  onSubmit,
  labelsList = [],
  getLabelsListRequest,
  setPhoneNumberLabelRequestStatus,
  bulkSetPhoneNumberLabelRequestStatus,
}) => {
  const [selectedLabelName, setSelectedLabelName] = useState<string>('');
  const [isNewLabel, setIsNewLabel] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const validateLabelName = useCallback((labelName: string) => {
    const trimmedLabel = labelName?.trim() || '';
    const isValidLength = trimmedLabel.length >= 2 && trimmedLabel.length <= 30;
    setHasError(!isValidLength && trimmedLabel.length > 0);
  }, []);


  // Fetch labels when modal opens
  useEffect(() => {
    if (show && getLabelsListRequest && labelsList.length === 0) {
      getLabelsListRequest();
    }
  }, [show, getLabelsListRequest, labelsList]);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setSelectedLabelName('');
      setIsNewLabel(false);
    }
  }, [show]);

  // Map labels to InlineAutosuggest items
  // Show creatable option only if labels array is empty (0 items)
  // API returns labels as string array: ["Mobile Number", "Sales Team Numbers", ...]
  const labelItems = useMemo<InlineAutosuggestItem[]>(() => {
    if (!labelsList || labelsList.length === 0) {
      // Return empty array to allow creating new labels when no labels exist
      return [];
    }
    return (labelsList || []).map((label: string) => ({
      name: label || '',
    }));
  }, [labelsList]);

  const handleConfirm = () => {
    validateLabelName(selectedLabelName || '');
    if (selectedLabelName?.trim() && !hasError) {
      onSubmit?.(selectedLabelName?.trim(), isNewLabel);
      // Don't close modal here - it will close on success in the parent component
    }
  };

  const handleCancel = () => {
    setSelectedLabelName('');
    setIsNewLabel(false);
    onClose();
  };

  const isLoading = getIsRequestPending(setPhoneNumberLabelRequestStatus?.status) 
  || getIsRequestPending(bulkSetPhoneNumberLabelRequestStatus);

  return (
    <Modal
      show={show}
      onHide={handleCancel}
      aria-labelledby="assign-phone-number-label-modal-title"
      className="assign-phone-number-label-modal"
      backdrop="static"
      centered
    >
      <Modal.Header className="assign-phone-number-label-modal__header">
        <div className="assign-phone-number-label-modal__header-content">
          <LabelIcon width={20} height={20} className="assign-phone-number-label-modal__header-icon" />
          <Modal.Title id="assign-phone-number-label-modal-title" className="assign-phone-number-label-modal__title">
            Assign Phone Number Label
          </Modal.Title>
        </div>
      </Modal.Header>
      <Modal.Body className="assign-phone-number-label-modal__body">
        <p className="assign-phone-number-label-modal__description">
          Assign a label to the selected phone numbers for better organization and identification.
        </p>
        <div className={`assign-phone-number-label-modal__input-container ${hasError ? 'assign-phone-number-label-modal__input-container--error' : ''}`}>
          <span className="assign-phone-number-label-modal__label">
            Select or Create Label
          </span>
          <InlineAutosuggest
            items={labelItems as InlineAutosuggestItem[]}
            placeholder="Choose existing or type new label name"
            value={selectedLabelName}
            onChange={(text) => {
              setHasError(false);
              const q = (text || '').trim().toLowerCase();
              const hasExact = (labelItems || []).some(
                (t) => (t?.name || '').trim().toLowerCase() === q,
              );
              setSelectedLabelName(text);
              // Only allow new label creation if labels list is empty (0 items)
              // Match reference pattern: isNewColumnName is set when q !== '' && !hasExact
              setIsNewLabel(q !== '' && !hasExact && labelsList?.length === 0);
            }}
            onPickExisting={(item) => {
              setSelectedLabelName(item?.name || '');
              setIsNewLabel(false);
            }}
            onPickNew={(name) => {
              // Only allow creating new labels if labels list is empty (0 items)
              validateLabelName(name || '');
              if (labelsList?.length === 0 && !hasError) {
                setSelectedLabelName(name);
                setIsNewLabel(true);
              }
            }}
            onClear={() => {
              setSelectedLabelName('');
              setIsNewLabel(false);
            }}
            onBlur={() => {
              validateLabelName(selectedLabelName || '');
            }}
            className="assign-phone-number-label-modal__autosuggest"
            createText="Add"
          />
          {hasError && (
            <div className="assign-phone-number-label-modal__error-message">
              {hasError ? 'Label length must be between 2 and 30 characters' : ''}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="assign-phone-number-label-modal__footer">
        <div className="assign-phone-number-label-modal__footer-actions">
          <Button
            onClick={handleCancel}
            variant="secondary"
            className="assign-phone-number-label-modal__cancel-btn"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            className="assign-phone-number-label-modal__confirm-btn"
            disabled={!selectedLabelName?.trim() || isLoading || hasError}
            isLoading={isLoading}
            loadingText="Confirm"
          >
            Confirm
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default memo(AssignPhoneNumberLabelModal);

